import { useState } from 'react'
import { AlertTriangle, CheckCircle2, TicketCheck, XCircle } from 'lucide-react'
import { Badge, Button, ErrorNote, Field, InfoNote, Input, Textarea } from '@/components/ui'
import { Modal } from '@/components/Modal'
import { useToast } from '@/components/Toast'
import { registerSlips } from '@/lib/api'
import { authErrorMessage } from '@/lib/authErrors'
import { formatCode } from '@shared/codes'
import {
  MAX_SLIPS,
  SLIP_OUTCOME_HELP,
  SLIP_OUTCOME_LABELS,
  isSlipSettled,
  parseSlipCodes,
  type SlipResult,
} from '@shared/slips'

/**
 * Registrering av fysiska lappar.
 *
 * Samma ruta för båda som får göra det: föräldern registrerar på sig själv,
 * lagledaren åt någon som inte fixar det. Två separata rutor hade drivit isär
 * ordval och felmeddelanden för samma handling.
 *
 * Rutan är byggd runt att man sitter med en bunt lappar i handen. Därför en
 * fritextruta och inte fem fält: man skriver av dem i den takt man orkar,
 * avgränsat hur som helst, och får en rad tillbaka per lapp. En felläst kod
 * bland fem ska inte kasta de fyra andra – det är det som gör att man vågar
 * skriva in allihop på en gång.
 */
export function RegisterSlipsModal({
  mode,
  onClose,
  onDone,
}: {
  /** `self` = föräldern registrerar sina egna. `onBehalf` = lagledaren hjälper till. */
  mode: 'self' | 'onBehalf'
  onClose: () => void
  onDone?: () => void
}) {
  const toast = useToast()

  const [raw, setRaw] = useState('')
  const [email, setEmail] = useState('')
  const [playerName, setPlayerName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [results, setResults] = useState<SlipResult[] | null>(null)

  const codes = parseSlipCodes(raw)
  const tooMany = codes.length > MAX_SLIPS
  const canSubmit =
    codes.length > 0 && !tooMany && !busy && (mode === 'self' || email.trim().length > 0)

  async function submit() {
    setError(null)
    setBusy(true)
    try {
      const response = await registerSlips({
        codes,
        forEmail: mode === 'onBehalf' ? email.trim() : undefined,
        playerName: playerName.trim() || undefined,
      })
      setResults(response.results)

      if (response.registered > 0) {
        toast.success(
          response.registered === 1
            ? 'Häftet är registrerat.'
            : `${response.registered} häften är registrerade.`,
        )
        onDone?.()
      }
    } catch (caught) {
      setError(authErrorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  /* ---------- Kvittot ---------- */

  if (results) {
    const ok = results.filter((result) => isSlipSettled(result.outcome))
    const failed = results.filter((result) => !isSlipSettled(result.outcome))

    return (
      <Modal
        open
        onClose={onClose}
        title={failed.length === 0 ? 'Klart' : 'Nästan klart'}
        size="sm"
        footer={
          <div className="flex gap-2">
            {failed.length > 0 && (
              <Button
                variant="quiet"
                fullWidth
                onClick={() => {
                  // Bara de som inte gick igenom blir kvar i rutan, så att man
                  // rättar dem i stället för att skriva om hela bunten.
                  setRaw(failed.map((result) => result.code).join('\n'))
                  setResults(null)
                }}
              >
                Rätta {failed.length}
              </Button>
            )}
            <Button fullWidth onClick={onClose}>
              Stäng
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {ok.length > 0 && (
            <p className="text-sm text-ink">
              <strong>{ok.length}</strong>{' '}
              {ok.length === 1 ? 'häfte ligger' : 'häften ligger'} nu{' '}
              {mode === 'self' ? 'på ditt konto' : 'på förälderns konto'}.
            </p>
          )}

          <ul className="space-y-2">
            {results.map((result) => {
              const settled = isSlipSettled(result.outcome)
              return (
                <li
                  key={result.code}
                  className="flex gap-2.5 rounded-card border border-border bg-surface p-3"
                >
                  <span className="mt-0.5 shrink-0">
                    {settled ? (
                      <CheckCircle2 size={17} className="text-success" />
                    ) : (
                      <XCircle size={17} className="text-danger" />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm font-bold tracking-widest">
                        {result.outcome === 'unknown' ? result.code : formatCode(result.code)}
                      </span>
                      <Badge tone={settled ? 'success' : 'warning'}>
                        {SLIP_OUTCOME_LABELS[result.outcome]}
                      </Badge>
                    </div>
                    {!settled && (
                      <p className="mt-1 text-xs text-muted">
                        {SLIP_OUTCOME_HELP[result.outcome]}
                      </p>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      </Modal>
    )
  }

  /* ---------- Formuläret ---------- */

  return (
    <Modal
      open
      onClose={onClose}
      title={mode === 'self' ? 'Registrera dina häften' : 'Registrera åt en förälder'}
      size="sm"
      footer={
        <div className="flex gap-2">
          <Button variant="quiet" fullWidth onClick={onClose}>
            Avbryt
          </Button>
          <Button
            fullWidth
            loading={busy}
            disabled={!canSubmit}
            icon={<TicketCheck size={16} />}
            onClick={() => void submit()}
          >
            Registrera{codes.length > 0 ? ` ${codes.length}` : ''}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}

        <p className="text-sm text-muted">
          {mode === 'self'
            ? 'Skriv av koderna på lapparna du fått. Då vet både du och lagledaren vilka häften som är dina.'
            : 'Skriv in förälderns e-post och koderna på lapparna hen fått. Hen får ett mail med sina häften.'}
        </p>

        {mode === 'onBehalf' && (
          <Field
            label="Förälderns e-post"
            hint="Saknas konto skapas ett, och hen får välja lösenord i mailet."
          >
            {({ id }) => (
              <Input
                id={id}
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="namn@exempel.se"
                autoComplete="off"
              />
            )}
          </Field>
        )}

        <Field
          label="Spelarens namn"
          hint="Frivilligt, men gör lagets avstämning mycket enklare."
        >
          {({ id }) => (
            <Input
              id={id}
              value={playerName}
              onChange={(event) => setPlayerName(event.target.value)}
              placeholder="Alva Lindqvist"
              autoComplete="off"
            />
          )}
        </Field>

        <Field
          label="Koder"
          hint="En per rad. Bindestreck och LB- spelar ingen roll."
          error={tooMany ? `Som mest ${MAX_SLIPS} lappar åt gången.` : undefined}
        >
          {({ id }) => (
            <Textarea
              id={id}
              rows={5}
              value={raw}
              onChange={(event) => setRaw(event.target.value)}
              placeholder={'LB-4K7M-2Q9X\nLB-8T3P-R5WN'}
              autoComplete="off"
              spellCheck={false}
              className="font-mono tracking-widest"
            />
          )}
        </Field>

        {codes.length > 0 && !tooMany && (
          <p className="text-xs text-muted">
            {codes.length} {codes.length === 1 ? 'kod' : 'koder'} inskrivna.
          </p>
        )}

        {/*
         * Den enda varningen i hela appen som står i formuläret och inte i
         * hjälpen. Att aktivera ett häfte man tänkt sälja går inte att ångra,
         * och orden "registrera" och "aktivera" ligger nära nog varandra för
         * att någon ska hinna göra fel innan hen läst hjälpen.
         */}
        <InfoNote>
          <span className="flex gap-2">
            <AlertTriangle size={16} className="mt-px shrink-0" />
            <span>
              Registrera är inte samma sak som att aktivera. Häftena förblir oanvända och går
              att sälja – det är <strong>köparen</strong> som aktiverar sitt häfte.
            </span>
          </span>
        </InfoNote>
      </div>
    </Modal>
  )
}
